/**
 * Change Order Service
 * Handles API calls for Change Orders:
 * - SCO: Subcontract Change Orders
 * - OCO: Owner Change Orders (Prime Contract Change Orders)
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3003'

const getAccessToken = () => {
  return localStorage.getItem('aps_access_token')
}

/**
 * Generic function to fetch change orders by type
 * @param {string} projectId - Project ID
 * @param {string} changeOrderType - Type: 'sco' or 'oco'
 * @param {string} status - Filter by status (optional)
 * @param {number} limit - Number of change orders to fetch
 * @returns {Promise<Array>} Array of change orders
 */
const fetchChangeOrdersByType = async (projectId, changeOrderType, status = null, limit = 100) => {
  // Validate required params
  if (!projectId) {
    console.error('[changeOrderService] Missing projectId')
    throw new Error('Project ID is required')
  }

  if (!changeOrderType || !['sco', 'oco'].includes(changeOrderType)) {
    console.error('[changeOrderService] Invalid changeOrderType:', changeOrderType)
    throw new Error('Invalid change order type. Must be "sco" or "oco"')
  }

  const token = getAccessToken()

  if (!token) {
    throw new Error('No access token available. Please log in.')
  }

  const endpoint = changeOrderType === 'oco' ? 'change-orders-oco' : 'change-orders'
  let url = `${API_BASE_URL}/api/cost/${projectId}/${endpoint}?limit=${limit}`

  if (status) {
    url += `&status=${status}`
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  })

  if (!response.ok) {
    // For 403 errors (permission denied), return empty array instead of throwing
    // This is expected for projects where user doesn't have Cost Management access
    if (response.status === 403) {
      console.warn(`[changeOrderService] No access to ${changeOrderType.toUpperCase()} for project ${projectId}, returning empty list`)
      return []
    }

    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    throw new Error(error.error || `Failed to fetch ${changeOrderType.toUpperCase()} (${response.status})`)
  }

  const data = await response.json()
  return data.changeOrders || []
}

/**
 * Get all SCO (Subcontract Change Orders) for a project
 * @param {string} projectId - Project ID
 * @param {string} status - Filter by status (optional)
 * @param {number} limit - Number of change orders to fetch
 * @returns {Promise<Array>} Array of SCO change orders
 */
export const getProjectChangeOrders = async (projectId, status = null, limit = 100) => {
  return fetchChangeOrdersByType(projectId, 'sco', status, limit)
}

/**
 * Get all OCO (Owner Change Orders / Prime Contract Change Orders) for a project
 * @param {string} projectId - Project ID
 * @param {string} status - Filter by status (optional)
 * @param {number} limit - Number of change orders to fetch
 * @returns {Promise<Array>} Array of OCO change orders
 */
export const getProjectOwnerChangeOrders = async (projectId, status = null, limit = 100) => {
  return fetchChangeOrdersByType(projectId, 'oco', status, limit)
}

/**
 * Get all change orders (both SCO and OCO) for a project
 * @param {string} projectId - Project ID
 * @param {string} status - Filter by status (optional)
 * @param {number} limit - Number of change orders to fetch per type
 * @returns {Promise<Array>} Combined array of all change orders
 */
export const getAllChangeOrders = async (projectId, status = null, limit = 100) => {
  if (!projectId) {
    console.error('[changeOrderService] getAllChangeOrders called without projectId')
    return []
  }

  try {
    const [scoOrders, ocoOrders] = await Promise.all([
      fetchChangeOrdersByType(projectId, 'sco', status, limit).catch(() => []),
      fetchChangeOrdersByType(projectId, 'oco', status, limit).catch(() => [])
    ])

    // Mark each type for identification
    const markedSCO = scoOrders.map(order => ({ ...order, _changeOrderType: 'SCO' }))
    const markedOCO = ocoOrders.map(order => ({ ...order, _changeOrderType: 'OCO' }))

    return [...markedSCO, ...markedOCO]
  } catch (error) {
    console.error('[changeOrderService] Error fetching all change orders:', error)
    // Don't throw - return empty array to prevent breaking the UI
    return []
  }
}
